/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  ActionEnum,
  ComplianceRuleType,
  DlpExchangePolicyItem,
  DlpPolicyFilters,
  DlpTemplate,
  DlpTemplateItem,
  DpFilter,
  MipLabelItem,
  Notification,
  TmDpfilterRule,
  TmMipLabelRule,
  PolicyFilters,
} from 'src/pages/Policy/utils/types';
import i18nInst from 'src/i18n';
import { MipLabelApi } from '@Apis';
import {
  FilterValidator,
  checkChangeRecipientAction,
  checkNotification,
  addEmails,
  removeItem,
  showAddApprovedSenderExceedListLimitNotification,
  showAddApprovedSenderListErrNotification,
} from '@Policy/utils/fns';

export const createDlpFilterSlice = (
  setPolicy: any,
  getPolicy: any,
  getAll: any
) => {
  const { t } = i18nInst;
  return {
    setDpState: (payload: Partial<DpFilter>) => {
      setPolicy((state: DlpExchangePolicyItem) => ({
        ...state,
        tmDpfilters: {
          ...state.tmDpfilters,
          ...payload,
        },
      }));
    },
    setDpNtfState: (payload: Partial<Notification>) => {
      setPolicy((state: DlpExchangePolicyItem) => ({
        ...state,
        tmDpfilters: {
          ...state.tmDpfilters,
          notification: {
            ...state.tmDpfilters.notification,
            ...payload,
          },
        },
      }));
    },
    setDlpTemplateAction: (templateId: string, action: ActionEnum) => {
      setPolicy((state: DlpExchangePolicyItem) => {
        const tmDpfilterrules = state.tmDpfilters.tmDpfilterrules;
        const index = tmDpfilterrules.findIndex(
          (item) => item.tmDpfiltertemplate.cfgIndex === templateId
        );
        if (index === -1) {
          return;
        }
        state.tmDpfilters.tmDpfilterrules[index].dpAction = action;
      });
    },
    setMipLabelAction: (labelId: string, action: ActionEnum) => {
      setPolicy((state: DlpExchangePolicyItem) => {
        const tmMipLabelRules = state.tmDpfilters.tmMipLabelRules;
        const index = tmMipLabelRules.findIndex(
          (item) => item.tmMipLabel.labelId === labelId
        );
        if (index === -1) {
          return;
        }
        state.tmDpfilters.tmMipLabelRules[index].dpAction = action;
      });
    },
    addDlpTemplatesAndMipLabels: (
      templates: (DlpTemplateItem | MipLabelItem)[]
    ) => {
      if (templates.length === 0) {
        return;
      }
      const policy = getPolicy();
      const store = getAll();
      const { addDlpTemplates } = store;
      const dlpTemplates: DlpTemplateItem[] = [];
      const mipLabels: MipLabelItem[] = [];
      templates.forEach((item) => {
        if (item.type === ComplianceRuleType.dlpTemplate) {
          dlpTemplates.push(item as DlpTemplateItem);
        } else {
          mipLabels.push(item as MipLabelItem);
        }
      });
      const originMipLabelKeys = policy.tmDpfilters.tmMipLabelRules.map(
        (item: TmMipLabelRule) => item.tmMipLabel.labelId
      );
      const newMipLabels = mipLabels.map((item) => ({
        tmMipLabel: {
          labelId: item.key,
        },
        dpAction: ActionEnum.pass, // use pass as default action
      }));
      const addMipLabels = newMipLabels.filter(
        (item) => !originMipLabelKeys.includes(item.tmMipLabel.labelId)
      );
      setPolicy((state: DlpExchangePolicyItem) => {
        state.tmDpfilters.tmMipLabelRules.push(...addMipLabels);
      });

      addDlpTemplates(dlpTemplates);
    },
    addDlpTemplates: (templates: DlpTemplateItem[]) => {
      if (templates.length === 0) {
        return;
      }
      const policy = getPolicy();

      const newTemplates = templates.map((item) => ({
        tmDpfiltertemplate: {
          cfgIndex: item.key,
        },
        dpAction: ActionEnum.pass, // use pass as default action
      }));
      const originTemplateKeys = policy.tmDpfilters.tmDpfilterrules.map(
        (item: TmDpfilterRule) => item.tmDpfiltertemplate.cfgIndex
      );
      const addTemplates = newTemplates.filter(
        (item) => !originTemplateKeys.includes(item.tmDpfiltertemplate.cfgIndex)
      );
      setPolicy((state: DlpExchangePolicyItem) => {
        state.tmDpfilters.tmDpfilterrules.push(...addTemplates);
      });
    },
    deleteDlpTemplate: (templateId: string) => {
      setPolicy((state: DlpExchangePolicyItem) => {
        const tmDpfilterrules = state.tmDpfilters.tmDpfilterrules;
        const index = tmDpfilterrules.findIndex(
          (item) => item.tmDpfiltertemplate.cfgIndex === templateId
        );
        if (index === -1) {
          return;
        }
        state.tmDpfilters.tmDpfilterrules.splice(index, 1);
      });
    },
    deleteMipLabel: (labelId: string) => {
      setPolicy((state: DlpExchangePolicyItem) => {
        const tmMipLabelRules = state.tmDpfilters.tmMipLabelRules;
        const index = tmMipLabelRules.findIndex(
          (item) => item.tmMipLabel.labelId === labelId
        );
        if (index === -1) {
          return;
        }
        state.tmDpfilters.tmMipLabelRules.splice(index, 1);
      });
    },
    validateDlp: () => {
      const currentState = getPolicy();
      const filter = currentState.tmDpfilters;
      const filterName = t('policy.filterName.dlp');
      const validator = new FilterValidator(DlpPolicyFilters.tmDpfilters);
      validator.add(filter.notification, 'checkNotification');
      validator.add(filter, 'checkChangeRecipientAction');
      validator.add(
        { keyValue: filter.dpReplacementText, keyName: 'dpReplacementText' },
        'checkToken'
      );
      validator.add(
        { keyValue: filter.dpReplacementText, keyName: 'dpReplacementText' },
        'checkReplacementText'
      );
      validator.add(filter, 'checkDlpScanTargetHeader');
      const result = validator.check();
      return result;
    },
    syncLabels: async () => {
      try {
        const currentStore = getAll();
        const { getMipLabels } = currentStore;
        await MipLabelApi.syncLabels();
        await getMipLabels();
      } catch (error) {
        console.error(error);
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      }
    },
    addDlpApprovedSender: (email: string | string[]) => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const includeDomain = false;
      const separator = ';';
      const {
        tmDpfilters: { dpApprovedSenderEmailEntries },
      } = currentState;
      const {
        config: { limitation },
      } = currentStore;
      const isSupportWildcard = true;
      const { errList, entriesStr, exceedListLimit } = addEmails(
        email,
        dpApprovedSenderEmailEntries,
        includeDomain,
        limitation.singleApprovedListMaxCount,
        separator,
        limitation.approvedListMaxCount,
        isSupportWildcard
      );
      setPolicy((state: DlpExchangePolicyItem) => ({
        ...state,
        tmDpfilters: {
          ...state.tmDpfilters,
          dpApprovedSenderEmailEntries: entriesStr,
        },
      }));
      // process exceedLimit
      if (exceedListLimit) {
        showAddApprovedSenderExceedListLimitNotification(
          limitation.approvedListMaxCount
        );
      }
      // process error list
      if (errList.length) {
        console.error('addDlpApprovedSender error list:', errList);
        showAddApprovedSenderListErrNotification(errList);
      }
    },
    removeDlpApprovedSender: (email: string) => {
      const deleteEmail = email.trim();
      if (deleteEmail === '') {
        return;
      }
      const currentState = getPolicy();
      const {
        tmDpfilters: { dpApprovedSenderEmailEntries },
      } = currentState;
      const separator = ';';
      const result = removeItem(
        deleteEmail,
        dpApprovedSenderEmailEntries,
        separator
      );
      setPolicy((state: DlpExchangePolicyItem) => ({
        ...state,
        tmDpfilters: {
          ...state.tmDpfilters,
          dpApprovedSenderEmailEntries: result,
        },
      }));
    },
    importDlpApprovedSender: (emails: string[]) => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const separator = ';';
      const {
        tmDpfilters: { dpApprovedSenderEmailEntries },
      } = currentState;
      const {
        config: { limitation },
      } = currentStore;
      const importEmails = emails.filter((email) => email.length > 0);
      const existedEntries = dpApprovedSenderEmailEntries
        .split(separator)
        .filter((email: string) => email.length > 0);

      // process exceedLimit
      if (
        importEmails.length + existedEntries.length >
        limitation.approvedListMaxCount
      ) {
        showAddApprovedSenderExceedListLimitNotification(
          limitation.approvedListMaxCount
        );
        return;
      }
      // add emails
      currentStore.addDlpApprovedSender(importEmails);
    },
  };
};
